import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent as EventoDeTecla } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';

import { Icon } from '@ds/core';
import { useAviso } from '@/comuns/toast';
import {
    ErroDoBrain, PREFERENCIAS_PADRAO, explicarErroDoBrain, normalizarPreferencias, novoPedidoId, problemaDoAnexo, textoParaFala,
} from '@/data/brain';
import type {
    AnexoDoBrain, ArquivoGeradoDoBrain, ConversaDoBrain, ErroExplicado, IdiomaDoBrain, MensagemDoBrain, ModelosDoBrain,
    PedidoDoBrain, PreferenciasDoBrain, ProjetoDoBrain, PropostaDoBrain,
} from '@/data/brain';
import { dados } from '@/data/porta';
import { atual, impersonacaoAtual } from '@/session/sessao';
import { useEscuro } from '@/tema';
import { ACOES_RAPIDAS } from './acoes';
import type { AcaoRapida } from './acoes';
import { carregarFonteDoBrain, useConsultaDeMidia } from './ambiente';
import { AssistenteDeTexto } from './AssistenteDeTexto';
import { b } from './classes';
import { MarkdownSeguro } from './MarkdownSeguro';
import { MenuDoMais } from './MenuDoMais';
import type { FerramentaDoCampo } from './MenuDoMais';
import { DialogoDeProjetos, PaginaDeConversas, PaginaDeProjetos } from './Paginas';
import { AjudaDoBrain, BibliotecaDeAnexos, BotaoOuvir, CartaoDeAcao, ConfirmarNoBrain, FolhaDoArquivo, MenuDaConta, iconeDoArquivo, tipoDoArquivo } from './Pecas';
import { AvisoDeCanais, CanalDaProposta } from './CanaisDesconectados';
import { CentralDePersonalizacao } from './Personalizar';
import type { SecaoDoPersonalizar } from './Personalizar';
import { PrevisaoDosAnexos } from './PrevisaoDosAnexos';
import { ESCOLHA_PADRAO, SeletorDeModelo, nomeDaEscolha } from './SeletorDeModelo';
import type { EscolhaDeModelo } from './SeletorDeModelo';
import { Dica, Girador, estiloAncorado, useFecharPorFora, useRetanguloDaAncora, useSetasNoMenu } from './ui/camadas';
import { LogoDoBrain } from './ui/marca';
import { AvisoDaVoz } from './voz/DialogosDaVoz';
import { ModoDeVoz } from './voz/ModoDeVoz';
import { useAcoesDaVoz } from './voz/useAcoesDaVoz';
import { useConversaPorVoz } from './voz/useConversaPorVoz';
import { formatarDuracao, useDitado } from './voz/useDitado';
import './estilo/tokens.css';
import './estilo/shell.css';
import './estilo/subpaginas.css';
import './estilo/camadas.css';
import './estilo/porte.css';

/**
 * O Brain — porte da tela `AiBrain` do Brain original para dentro da VecchIA,
 * com o visual dele (exceção decidida em 13/09/2026, ver CLAUDE.md) e as regras
 * da VecchIA por baixo: um login, dados só da pessoa, teto de IA, markdown sem
 * HTML, somente leitura na impersonação e ação que só executa no clique.
 *
 * Na tela a assistente se chama VecchIA (13/09/2026); a rota e o código seguem "brain".
 * Duas entradas: a rota `/app/brain`, dentro da shell, e o orbe da topbar, que abre esta
 * mesma tela cobrindo a janela inteira (`sobreposta`), de qualquer tela da clínica.
 */

export interface BrainAppProps {
    /** Aberta pelo orbe da topbar, cobrindo a janela: ganha o botão de fechar. */
    sobreposta?: boolean;
    aoFechar?: () => void;
    /** Conversa a abrir ao montar (a tela cheia lê do endereço). */
    conversaInicial?: string | null;
    /** A tela cheia guarda a conversa no endereço. */
    aoMudarConversa?: (conversaId: string | null) => void;
    /** Aberta pela topbar, as sugestões do módulo de onde a pessoa veio no lugar das ações rápidas. */
    sugestoesDoContexto?: AcaoRapida[];
    /**
     * No celular, abre direto na conversa por voz (pedido de 16/09/2026), sem o aviso de
     * requisitos. Fora do celular, sem voz disponível ou em impersonação, abre o chat.
     */
    abrirNaVoz?: boolean;
}

type Visao = 'chat' | 'conversas' | 'projetos' | 'personalizar';

interface MensagemVisivel extends MensagemDoBrain {
    /** A fala transcrita da conversa por voz não aparece no chat; só a resposta. */
    oculta?: boolean;
    /** Ainda não confirmada pelo servidor: não dá para editar a partir dela. */
    local?: boolean;
    /** Aviso de "parar", com o pedido para reencontrar a resposta. */
    interrompida?: string;
}

interface AnexoPendente {
    id: string;
    arquivo: File;
    rotulo: string;
    origem: 'local' | 'captura' | 'aprender';
    previa: string | null;
}

interface EnvioEmAndamento {
    pedidoId: string;
    controlador: AbortController;
    enviadoEm: number;
    idLocal: string;
    recuperado: PedidoDoBrain | null;
}

const IDIOMAS: { codigo: IdiomaDoBrain; rotulo: string }[] = [
    { codigo: 'pt-BR', rotulo: 'Português' },
    { codigo: 'en', rotulo: 'English' },
    { codigo: 'es', rotulo: 'Español' },
];

/* Chaves de conveniência no navegador: a verdade é do servidor. */
const CHAVE_PREFERENCIAS = 'brain-preferencias';
const CHAVE_MODELO = 'brain-modelo';
const CHAVE_PROJETO = 'brain-projeto-ativo';

function lerLocal<T>(chave: string, padrao: T): T {
    try {
        const bruto = localStorage.getItem(chave);
        return bruto ? JSON.parse(bruto) as T : padrao;
    } catch {
        return padrao;
    }
}

function gravarLocal(chave: string, valor: unknown): void {
    try {
        localStorage.setItem(chave, JSON.stringify(valor));
    } catch {
        /* Sem armazenamento: vale o que veio do servidor. */
    }
}

function mensagemDe(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}

function eInterrupcao(e: unknown): boolean {
    return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError';
}

/** Queda de conexão: o pedido não chegou a ter resposta do servidor. */
function eQuedaDeConexao(e: unknown): boolean {
    if (e instanceof ErroDoBrain) return e.status === 0 && /fetch|network|rede|conex/i.test(e.message);
    return e instanceof TypeError;
}

function esperar(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
}

/** A resposta que o leitor de tela anuncia: o começo do texto e as ações pendentes. */
function anuncioDaResposta(m: MensagemDoBrain): string {
    const texto = textoParaFala(m.conteudo);
    const resumo = texto.length > 400 ? `${texto.slice(0, 400)}…` : texto;
    const pendentes = m.propostas.filter((p) => p.situacao === 'pendente').length;
    const acoes = pendentes === 0 ? '' : pendentes === 1 ? ' Há uma ação aguardando a sua confirmação.' : ` Há ${String(pendentes)} ações aguardando a sua confirmação.`;
    return `Resposta da VecchIA: ${resumo}${acoes}`;
}

/** As etiquetas acima da resposta (no original, as ferramentas usadas). */
function etiquetasDa(m: MensagemVisivel): string[] {
    const etiquetas: string[] = [];
    if (m.fontes && m.fontes.length > 0) etiquetas.push('Busca na web');
    if (m.documentos && m.documentos.length > 0) etiquetas.push('Documentos');
    if (m.arquivos.length > 0) etiquetas.push('Arquivo');
    if (m.propostas.length > 0) etiquetas.push('Ação proposta');
    return etiquetas;
}

export function BrainApp({ sobreposta = false, aoFechar, conversaInicial = null, aoMudarConversa, sugestoesDoContexto, abrirNaVoz = false }: BrainAppProps) {
    const navigate = useNavigate();
    const aviso = useAviso();
    const escuro = useEscuro();
    const celular = useConsultaDeMidia('(max-width: 600px)');
    /** Tela de toque sem mouse: Enter quebra linha e o envio é pelo botão. */
    const toque = useConsultaDeMidia('(hover: none) and (pointer: coarse)');

    const sessao = atual();
    const impersonacao = impersonacaoAtual();
    const somenteLeitura = impersonacao
        ? `Você está em ${impersonacao.clinica} por impersonação, só para leitura: a VecchIA não conversa nem executa ações por esta clínica.`
        : null;
    const demonstracao = dados().brainDemonstracao();
    const sufixo = sessao?.clinicaId ?? sessao?.clinica ?? 'sem-clinica';

    const [mensagem, setMensagem] = useState('');
    const [carregando, setCarregando] = useState(false);
    const [conversas, setConversas] = useState<ConversaDoBrain[]>([]);
    const [carregandoConversas, setCarregandoConversas] = useState(true);
    const [conversaAtiva, setConversaAtiva] = useState<{ id: string; titulo: string } | null>(null);
    const [mensagens, setMensagens] = useState<MensagemVisivel[]>([]);
    const [modelos, setModelos] = useState<ModelosDoBrain | null>(null);
    const [escolha, setEscolha] = useState<EscolhaDeModelo>(() => lerLocal(`${CHAVE_MODELO}:${sufixo}`, ESCOLHA_PADRAO));
    const [anexos, setAnexos] = useState<AnexoPendente[]>([]);
    const [edicao, setEdicao] = useState<string | null>(null);
    const [ferramenta, setFerramenta] = useState<FerramentaDoCampo>(null);
    const [barraAberta, setBarraAberta] = useState(!celular);
    const [visao, setVisao] = useState<Visao>('chat');
    const [secao, setSecao] = useState<SecaoDoPersonalizar>('hub');
    const [projetos, setProjetos] = useState<ProjetoDoBrain[]>([]);
    const [carregandoProjetos, setCarregandoProjetos] = useState(true);
    const [projetoAtivo, setProjetoAtivo] = useState<string | null>(() => lerLocal<string | null>(`${CHAVE_PROJETO}:${sufixo}`, null));
    const [preferencias, setPreferencias] = useState<PreferenciasDoBrain>(() => normalizarPreferencias(lerLocal(`${CHAVE_PREFERENCIAS}:${sufixo}`, PREFERENCIAS_PADRAO)));
    const [dialogoDeProjetos, setDialogoDeProjetos] = useState(false);
    const [arquivoAberto, setArquivoAberto] = useState<ArquivoGeradoDoBrain | null>(null);
    const [bibliotecaAberta, setBibliotecaAberta] = useState(false);
    const [painelDeVoz, setPainelDeVoz] = useState(false);
    const [vozAtiva, setVozAtiva] = useState(false);
    const [avisoDaVoz, setAvisoDaVoz] = useState(false);
    const [ajuda, setAjuda] = useState(false);
    const [menuMais, setMenuMais] = useState(false);
    const [menuIdioma, setMenuIdioma] = useState(false);
    const [apagando, setApagando] = useState<ConversaDoBrain | null>(null);
    const [renomeando, setRenomeando] = useState<string | null>(null);
    const [tituloNovo, setTituloNovo] = useState('');
    const [erro, setErro] = useState<ErroExplicado | null>(null);
    const [aceita, setAceita] = useState('');
    const [anuncio, setAnuncio] = useState('');

    const campo = useRef<HTMLTextAreaElement>(null);
    const caixa = useRef<HTMLDivElement>(null);
    const fim = useRef<HTMLDivElement>(null);
    const seletorDeArquivo = useRef<HTMLInputElement>(null);
    const botaoMais = useRef<HTMLButtonElement>(null);
    const botaoIdioma = useRef<HTMLButtonElement>(null);
    const menuDeIdioma = useRef<HTMLDivElement>(null);
    const pendente = useRef<EnvioEmAndamento | null>(null);
    const conversaAtivaRef = useRef(conversaAtiva);
    conversaAtivaRef.current = conversaAtiva;
    const salvarPreferenciasTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const setasDoIdioma = useSetasNoMenu();

    const idioma = preferencias.idioma;
    const assistente = 'VecchIA';
    const primeiroNome = (sessao?.nome ?? '').split(' ')[0] || 'usuário';

    useEffect(() => {
        carregarFonteDoBrain();
    }, []);

    /* Ao virar celular o menu vira gaveta e começa fechado. */
    useEffect(() => {
        if (celular) setBarraAberta(false);
    }, [celular]);

    /* ------------------------------------------------------------ leitura */

    const recarregarConversas = useCallback(async (projeto: string | null = projetoAtivo) => {
        setCarregandoConversas(true);
        try {
            setConversas(await dados().brainConversas(projeto === null ? undefined : { projetoId: projeto }));
        } catch (e) {
            setConversas([]);
            setErro({ titulo: 'Não foi possível ler as conversas', detalhe: mensagemDe(e), tom: 'danger' });
        } finally {
            setCarregandoConversas(false);
        }
    }, [projetoAtivo]);

    const recarregarProjetos = useCallback(async () => {
        setCarregandoProjetos(true);
        try {
            const lista = await dados().brainProjetos();
            setProjetos(lista);
            /* Projeto guardado que foi arquivado em outro aparelho: volta a "todas". */
            setProjetoAtivo((atualId) => (atualId && !lista.some((p) => p.id === atualId) ? null : atualId));
        } catch {
            setProjetos([]);
        } finally {
            setCarregandoProjetos(false);
        }
    }, []);

    useEffect(() => {
        void recarregarProjetos();
        dados().brainModelos().then(setModelos).catch(() => setModelos({ provedores: [], padrao: null, documentos: false, voz: { transcrever: false, falar: false } }));
        dados().brainPreferencias()
            .then((p) => { setPreferencias(p); gravarLocal(`${CHAVE_PREFERENCIAS}:${sufixo}`, p); })
            .catch(() => undefined);
    }, [recarregarProjetos, sufixo]);

    useEffect(() => {
        void recarregarConversas(projetoAtivo);
        gravarLocal(`${CHAVE_PROJETO}:${sufixo}`, projetoAtivo);
    }, [projetoAtivo, recarregarConversas, sufixo]);

    useEffect(() => {
        gravarLocal(`${CHAVE_MODELO}:${sufixo}`, escolha);
    }, [escolha, sufixo]);

    /* Rolagem até a última mensagem. */
    useEffect(() => {
        const alvo = fim.current;
        if (alvo && typeof alvo.scrollIntoView === 'function') alvo.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [mensagens, carregando]);

    /* --------------------------------------------------------- preferências */

    const gravarPreferencias = useCallback(async (p: PreferenciasDoBrain, avisar: boolean) => {
        try {
            const salvas = await dados().brainSalvarPreferencias(p);
            gravarLocal(`${CHAVE_PREFERENCIAS}:${sufixo}`, salvas);
            if (avisar) aviso({ tone: 'success', title: 'Preferências salvas e aplicadas à VecchIA.' });
        } catch (e) {
            aviso({ tone: 'danger', title: 'Não foi possível salvar as preferências', description: mensagemDe(e) });
        }
    }, [aviso, sufixo]);

    const mudarPreferencias = useCallback((proximas: PreferenciasDoBrain) => {
        if (somenteLeitura) return;
        setPreferencias(proximas);
        if (salvarPreferenciasTimer.current) clearTimeout(salvarPreferenciasTimer.current);
        /* Cada toque grava, como no original; o campo de texto espera parar de digitar. */
        salvarPreferenciasTimer.current = setTimeout(() => { void gravarPreferencias(proximas, false); }, 600);
    }, [gravarPreferencias, somenteLeitura]);

    const salvarPreferenciasAgora = useCallback(() => {
        if (salvarPreferenciasTimer.current) clearTimeout(salvarPreferenciasTimer.current);
        salvarPreferenciasTimer.current = null;
        void gravarPreferencias(preferencias, true);
    }, [gravarPreferencias, preferencias]);

    useEffect(() => () => {
        if (salvarPreferenciasTimer.current) clearTimeout(salvarPreferenciasTimer.current);
    }, []);

    /* ------------------------------------------------------------ conversas */

    const novaConversa = useCallback(() => {
        setConversaAtiva(null);
        setMensagens([]);
        setMensagem('');
        setEdicao(null);
        setFerramenta(null);
        setErro(null);
        setAnexos((atuais) => {
            atuais.forEach((a) => { if (a.previa) URL.revokeObjectURL(a.previa); });
            return [];
        });
        aoMudarConversa?.(null);
    }, [aoMudarConversa]);

    const abrirConversa = useCallback(async (id: string) => {
        setVisao('chat');
        setErro(null);
        setEdicao(null);
        if (celular) setBarraAberta(false);
        try {
            const lidas = await dados().brainMensagens(id);
            const conhecida = conversas.find((c) => c.id === id);
            setConversaAtiva({ id, titulo: conhecida?.titulo ?? 'Conversa' });
            setMensagens(lidas);
            aoMudarConversa?.(id);
        } catch (e) {
            setErro({ titulo: 'Não foi possível abrir a conversa', detalhe: mensagemDe(e), tom: 'danger' });
        }
    }, [aoMudarConversa, celular, conversas]);

    /* A conversa do endereço (tela cheia), quando ela muda por fora. */
    useEffect(() => {
        if (conversaInicial && conversaInicial !== conversaAtivaRef.current?.id) void abrirConversa(conversaInicial);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só quando o endereço muda
    }, [conversaInicial]);

    /* O título da conversa aberta acompanha a lista (renomear, primeira resposta). */
    useEffect(() => {
        if (!conversaAtiva) return;
        const naLista = conversas.find((c) => c.id === conversaAtiva.id);
        if (naLista && naLista.titulo !== conversaAtiva.titulo) setConversaAtiva({ id: naLista.id, titulo: naLista.titulo });
    }, [conversas, conversaAtiva]);

    const confirmarApagar = async () => {
        const alvo = apagando;
        setApagando(null);
        if (!alvo) return;
        try {
            await dados().brainApagarConversa(alvo.id);
            aviso({ tone: 'success', title: 'Conversa apagada' });
            if (alvo.id === conversaAtiva?.id) novaConversa();
            await recarregarConversas();
        } catch (e) {
            aviso({ tone: 'danger', title: 'Não foi possível apagar a conversa', description: mensagemDe(e) });
        }
    };

    const salvarTitulo = async (id: string) => {
        const titulo = tituloNovo.trim();
        setRenomeando(null);
        if (!titulo) return;
        try {
            await dados().brainRenomearConversa(id, titulo);
            if (conversaAtiva?.id === id) setConversaAtiva({ id, titulo });
            await recarregarConversas();
            aviso({ tone: 'success', title: 'Renomeada.' });
        } catch (e) {
            aviso({ tone: 'danger', title: 'Não foi possível renomear', description: mensagemDe(e) });
        }
    };

    /* ------------------------------------------------------------ projetos */

    const escolherProjetoDaLista = (id: string | null) => {
        setProjetoAtivo(id);
        /* Trocar de projeto começa do zero, como no original. */
        novaConversa();
        setVisao('chat');
    };

    const escolherProjetoDoMenu = async (id: string | null) => {
        const aberta = conversaAtivaRef.current;
        setProjetoAtivo(id);
        if (!aberta) return;
        try {
            await dados().brainMoverConversa(aberta.id, id);
            const nome = id ? projetos.find((p) => p.id === id)?.nome ?? 'projeto' : null;
            aviso({ tone: 'success', title: nome ? `Conversa movida para “${nome}”.` : 'Conversa tirada do projeto.' });
        } catch (e) {
            aviso({ tone: 'danger', title: 'Não foi possível mudar a conversa de projeto', description: mensagemDe(e) });
        }
    };

    const criarProjeto = async (nome: string) => {
        const criado = await dados().brainCriarProjeto(nome);
        await recarregarProjetos();
        setProjetoAtivo(criado.id);
        aviso({ tone: 'success', title: 'Projeto criado e selecionado.' });
    };

    /* ------------------------------------------------------------ anexos */

    const adicionarArquivos = (lista: File[], origem: AnexoPendente['origem'], rotulo?: string) => {
        const aceitos: AnexoPendente[] = [];
        for (const arquivo of lista) {
            const problema = problemaDoAnexo(arquivo);
            if (problema) {
                aviso({ tone: 'danger', title: 'Anexo recusado', description: problema });
                continue;
            }
            aceitos.push({
                id: `${origem}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
                arquivo, origem, rotulo: rotulo ?? arquivo.name,
                previa: arquivo.type.startsWith('image/') && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(arquivo) : null,
            });
        }
        if (aceitos.length > 0) setAnexos((atuais) => [...atuais, ...aceitos]);
    };

    const aoEscolherArquivos = (e: ChangeEvent<HTMLInputElement>) => {
        adicionarArquivos(Array.from(e.target.files ?? []), 'local');
        e.target.value = '';
    };

    const tirarAnexo = (id: string) => {
        setAnexos((atuais) => {
            const saindo = atuais.find((a) => a.id === id);
            if (saindo?.previa) URL.revokeObjectURL(saindo.previa);
            return atuais.filter((a) => a.id !== id);
        });
    };

    const escolherTipo = (aceitaDoTipo: string) => {
        setAceita(aceitaDoTipo);
        setTimeout(() => seletorDeArquivo.current?.click(), 0);
    };

    const capturarTela = async () => {
        if (typeof navigator.mediaDevices?.getDisplayMedia !== 'function') {
            aviso({ tone: 'danger', title: 'Captura de tela não suportada neste navegador.' });
            return;
        }
        try {
            const fluxo = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            const video = document.createElement('video');
            video.srcObject = fluxo;
            video.muted = true;
            await new Promise<void>((resolve, reject) => {
                video.onloadedmetadata = () => { video.play().then(() => resolve(), reject); };
                video.onerror = () => reject(new Error('vídeo'));
            });
            await esperar(120);
            const tela = document.createElement('canvas');
            tela.width = video.videoWidth;
            tela.height = video.videoHeight;
            tela.getContext('2d')?.drawImage(video, 0, 0);
            fluxo.getTracks().forEach((t) => t.stop());
            const imagem = await new Promise<Blob | null>((resolve) => tela.toBlob(resolve, 'image/png'));
            if (!imagem) throw new Error('vazia');
            adicionarArquivos([new File([imagem], `captura-tela-${String(Date.now())}.png`, { type: 'image/png' })], 'captura');
            aviso({ tone: 'success', title: 'Captura de tela anexada.' });
        } catch (e) {
            if (!(typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'NotAllowedError')) {
                aviso({ tone: 'danger', title: 'Não foi possível capturar a tela.' });
            }
        }
    };

    const aprenderLink = async (url: string) => {
        const lido = await dados().brainAprenderLink(url);
        let anfitriao = 'url';
        try {
            anfitriao = new URL(lido.url).hostname.replace(/[^a-z0-9.-]/gi, '-');
        } catch {
            /* endereço sem host: fica "url" */
        }
        const cabecalho = `URL: ${lido.url}\nTítulo: ${lido.titulo}\n\n---\n\n`;
        adicionarArquivos([new File([cabecalho + lido.texto], `aprendizado-${anfitriao}.txt`, { type: 'text/plain' })], 'aprender', lido.titulo || url);
        aviso({
            tone: 'success', title: 'Conteúdo da URL adicionado como anexo.',
            ...(lido.truncado ? { description: 'A página era longa: entrou só o começo do texto.' } : {}),
        });
    };

    /* ------------------------------------------------------------ envio */

    const aplicarPedidoPronto = useCallback((pedido: Extract<PedidoDoBrain, { situacao: 'pronta' }>, idLocal: string) => {
        setMensagens((atuais) => {
            const sem = atuais.filter((m) => m.id !== idLocal && !m.interrompida);
            const ja = new Set(sem.map((m) => m.id));
            return [...sem, ...pedido.mensagens.filter((m) => !ja.has(m.id))];
        });
        const anterior = conversaAtivaRef.current;
        conversaAtivaRef.current = { id: pedido.conversaId, titulo: anterior?.id === pedido.conversaId ? anterior.titulo : 'Conversa' };
        setConversaAtiva((atualConversa) => ({ id: pedido.conversaId, titulo: atualConversa?.id === pedido.conversaId ? atualConversa.titulo : 'Conversa' }));
        aoMudarConversa?.(pedido.conversaId);
        const resposta = [...pedido.mensagens].reverse().find((m) => m.papel === 'assistente');
        if (resposta) setAnuncio(anuncioDaResposta(resposta));
        void recarregarConversas();
    }, [aoMudarConversa, recarregarConversas]);

    /** Depois de queda de conexão: procura a resposta que o servidor guardou (~2,5 min). */
    const reencontrar = useCallback(async (pedidoId: string, sinal: AbortSignal) => {
        for (let tentativa = 0; tentativa < 30; tentativa += 1) {
            await esperar(tentativa === 0 ? 1500 : 5000);
            if (sinal.aborted) return null;
            try {
                const pedido = await dados().brainPedido(pedidoId);
                if (pedido.situacao === 'pronta' || pedido.situacao === 'falhou') return pedido;
                if (pedido.situacao === 'desconhecido' && tentativa >= 2) return null;
            } catch {
                /* ainda sem conexão: tenta de novo */
            }
        }
        return null;
    }, []);

    const enviar = useCallback(async (texto: string, opcoes: { voz?: boolean } = {}): Promise<{ ok: boolean; resposta: string; fala: string | null }> => {
        const bruto = texto.trim();
        if (somenteLeitura) return { ok: false, resposta: somenteLeitura, fala: null };
        if (!bruto && anexos.length === 0) return { ok: false, resposta: '', fala: null };
        if (carregando && !opcoes.voz) return { ok: false, resposta: '', fala: null };

        const conteudo = bruto || 'Analise os arquivos anexados.';
        const idLocal = `local-${String(Date.now())}`;
        const pedidoId = novoPedidoId();
        const controlador = new AbortController();
        const envio: EnvioEmAndamento = { pedidoId, controlador, enviadoEm: Date.now(), idLocal, recuperado: null };
        pendente.current = envio;
        const aberta = conversaAtivaRef.current;
        const editar = aberta ? edicao : null;
        const pendentesDoCampo = anexos;
        const ferramentaDoEnvio = ferramenta;

        setErro(null);
        setCarregando(true);
        setMensagem('');
        setFerramenta(null);
        setEdicao(null);
        setAnexos([]);
        setMensagens((atuais) => [...atuais, {
            id: idLocal, papel: 'usuario', conteudo, criadoEm: new Date().toISOString(), anexos: [], arquivos: [], propostas: [],
            local: true, ...(opcoes.voz ? { oculta: true } : {}),
        }]);
        setAnuncio(`${assistente} está pensando…`);
        if (campo.current) campo.current.style.height = '36px';

        try {
            /* Os arquivos sobem ao balde privado antes da mensagem. */
            const subidos: AnexoDoBrain[] = [];
            for (const a of pendentesDoCampo) {
                subidos.push(await dados().brainEnviarAnexo(a.arquivo, aberta?.id));
                if (a.previa) URL.revokeObjectURL(a.previa);
            }
            if (subidos.length > 0) setMensagens((atuais) => atuais.map((m) => (m.id === idLocal ? { ...m, anexos: subidos } : m)));

            const resposta = await dados().brainEnviar({
                ...(aberta ? { conversaId: aberta.id } : {}),
                mensagem: conteudo,
                anexos: subidos,
                ...(escolha.provedor ? { provedor: escolha.provedor } : {}),
                modelo: escolha.modelo,
                idioma,
                ...(ferramentaDoEnvio === 'web' ? { buscarNaWeb: true } : {}),
                ...(ferramentaDoEnvio === 'documentos' ? { buscarNosDocumentos: true } : {}),
                ...(!aberta && projetoAtivo ? { projetoId: projetoAtivo } : {}),
                ...(editar ? { editarAPartirDe: editar } : {}),
                pedidoId,
                ...(opcoes.voz ? { modoVoz: true } : {}),
            }, controlador.signal);

            setMensagens((atuais) => [
                ...atuais.map((m) => (m.id === idLocal
                    ? { ...m, local: false, ...(resposta.mensagemDaPessoa ? { id: resposta.mensagemDaPessoa.id, criadoEm: resposta.mensagemDaPessoa.criadoEm } : {}) }
                    : m)),
                resposta.mensagem,
            ]);
            /* A ref vai junto, sem esperar o render: na conversa por voz a próxima pergunta pode sair
               logo em seguida e precisa cair na MESMA conversa, com esta troca no contexto. */
            const conversaDaResposta = { id: resposta.conversaId, titulo: aberta?.id === resposta.conversaId ? aberta.titulo : 'Conversa' };
            conversaAtivaRef.current = conversaDaResposta;
            setConversaAtiva(conversaDaResposta);
            if (resposta.conversaId !== aberta?.id) aoMudarConversa?.(resposta.conversaId);
            const primeiroArquivo = resposta.mensagem.arquivos[0];
            if (primeiroArquivo && !opcoes.voz) setArquivoAberto(primeiroArquivo);
            setAnuncio(anuncioDaResposta(resposta.mensagem));
            void recarregarConversas();
            return { ok: true, resposta: resposta.mensagem.conteudo, fala: resposta.fala };
        } catch (e) {
            if (eInterrupcao(e) && envio.recuperado?.situacao === 'pronta') {
                aplicarPedidoPronto(envio.recuperado, idLocal);
                return { ok: true, resposta: '', fala: envio.recuperado.fala ?? null };
            }
            if (eInterrupcao(e)) {
                setMensagens((atuais) => [...atuais, {
                    id: `interrompida-${pedidoId}`, papel: 'assistente', conteudo: 'Geração interrompida. Envie uma nova mensagem quando quiser.',
                    criadoEm: new Date().toISOString(), anexos: [], arquivos: [], propostas: [], local: true, interrompida: pedidoId,
                }]);
                setAnuncio('Geração interrompida.');
                return { ok: false, resposta: 'cancelled', fala: null };
            }
            let falha: unknown = e;
            const emAndamento = e instanceof ErroDoBrain && e.status === 409 && e.detalhes.situacao === 'processando';
            if (eQuedaDeConexao(e) || emAndamento) {
                /* A conexão caiu no meio: a resposta pode já estar guardada no servidor. */
                const achado = await reencontrar(pedidoId, controlador.signal);
                if (achado?.situacao === 'pronta') {
                    aplicarPedidoPronto(achado, idLocal);
                    return { ok: true, resposta: [...achado.mensagens].reverse().find((m) => m.papel === 'assistente')?.conteudo ?? '', fala: achado.fala ?? null };
                }
                if (achado?.situacao === 'falhou') falha = new ErroDoBrain(502, achado.erro);
            }
            const explicado = explicarErroDoBrain(falha);
            setErro(explicado);
            setAnuncio(`${explicado.titulo}. ${explicado.detalhe}`);
            /* O texto digitado não se perde: volta ao campo se ele ainda estiver vazio. */
            setMensagens((atuais) => atuais.filter((m) => m.id !== idLocal));
            setMensagem((atualTexto) => atualTexto || bruto);
            return { ok: false, resposta: explicado.detalhe, fala: null };
        } finally {
            if (pendente.current === envio) pendente.current = null;
            setCarregando(false);
        }
    }, [
        anexos, aoMudarConversa, aplicarPedidoPronto, assistente, carregando, edicao, escolha, ferramenta, idioma, projetoAtivo,
        recarregarConversas, reencontrar, somenteLeitura,
    ]);

    const parar = () => {
        pendente.current?.controlador.abort();
        setCarregando(false);
    };

    /* Com a tela bloqueada o celular pode deixar o pedido preso sem erro. Ao voltar,
       se a resposta já estiver guardada no servidor, usa ela e descarta o pedido. */
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const aoMudar = () => {
            const envio = pendente.current;
            if (document.visibilityState !== 'visible' || !envio || Date.now() - envio.enviadoEm < 8000) return;
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                if (pendente.current !== envio || envio.controlador.signal.aborted) return;
                dados().brainPedido(envio.pedidoId).then((pedido) => {
                    if (pedido.situacao === 'pronta' && pendente.current === envio && !envio.controlador.signal.aborted) {
                        envio.recuperado = pedido;
                        envio.controlador.abort();
                    }
                }).catch(() => undefined);
            }, 2500);
        };
        document.addEventListener('visibilitychange', aoMudar);
        return () => {
            if (timer) clearTimeout(timer);
            document.removeEventListener('visibilitychange', aoMudar);
        };
    }, []);

    const verSeFicouPronta = async (pedidoId: string) => {
        try {
            const pedido = await dados().brainPedido(pedidoId);
            if (pedido.situacao === 'pronta') {
                aplicarPedidoPronto(pedido, `interrompida-${pedidoId}`);
                return;
            }
            aviso({
                tone: pedido.situacao === 'falhou' ? 'danger' : 'info',
                title: pedido.situacao === 'processando' ? 'A resposta ainda está sendo gerada.' : pedido.situacao === 'falhou' ? 'A resposta falhou no servidor.' : 'O servidor não guardou resposta para este envio.',
                ...(pedido.situacao === 'falhou' ? { description: pedido.erro } : {}),
            });
        } catch (e) {
            aviso({ tone: 'danger', title: 'Não foi possível consultar a resposta', description: mensagemDe(e) });
        }
    };

    const editarMensagem = (indice: number) => {
        const alvo = mensagens[indice];
        if (!alvo || alvo.papel !== 'usuario' || alvo.local) return;
        setMensagens((atuais) => atuais.slice(0, indice));
        setEdicao(alvo.id);
        setMensagem(alvo.conteudo);
        focarCampo();
    };

    const cancelarEdicao = () => {
        setEdicao(null);
        if (conversaAtiva) void abrirConversa(conversaAtiva.id);
    };

    /* ------------------------------------------------------------ voz */

    const ditado = useDitado({ aoMudarTexto: setMensagem, transcrever: (audio) => dados().brainTranscrever(audio, idioma) });
    /* A frase ouvida decide a ação em foco antes de virar mensagem (`useAcoesDaVoz`, mais abaixo). */
    const resolverFalaDaVoz = useRef<(texto: string, contexto: { comecouEm: number; porCimaDaVoz: boolean }) => Promise<boolean>>(async () => false);
    const conversaPorVoz = useConversaPorVoz({
        idioma,
        enviar: (texto) => enviar(texto, { voz: true }),
        transcrever: (audio) => dados().brainTranscrever(audio, idioma),
        ...(modelos?.voz.falar === false ? {} : { falar: (texto: string, o: { idioma: IdiomaDoBrain }) => dados().brainFalar(texto, { ...o, fluxo: true }) }),
        aoIniciarSessao: novaConversa,
        aoOuvirFrase: (texto, contexto) => resolverFalaDaVoz.current(texto, contexto),
        aoEncerrar: () => {
            setVozAtiva(false);
            setPainelDeVoz(false);
        },
        ativa: vozAtiva,
    });

    const fecharVoz = () => {
        setVozAtiva(false);
        setPainelDeVoz(false);
        conversaPorVoz.parar();
    };

    const alternarVoz = () => {
        if (painelDeVoz) {
            fecharVoz();
            return;
        }
        if (!conversaPorVoz.suportada) {
            aviso({ tone: 'danger', title: 'Conversa por voz indisponível', description: 'Este navegador não grava áudio ou não tem voz. Use Chrome ou Edge com microfone.' });
            return;
        }
        if (modelos && !modelos.voz.transcrever) {
            aviso({ tone: 'danger', title: 'Conversa por voz indisponível', description: 'A transcrição da sua fala precisa de uma chave da OpenAI ou do Gemini ligada na plataforma.' });
            return;
        }
        setAvisoDaVoz(true);
    };

    const iniciarVoz = () => {
        if (ditado.gravando || ditado.salvando) {
            aviso({ tone: 'danger', title: 'Encerre o ditado por voz antes de iniciar a conversa.' });
            return;
        }
        setVozAtiva(true);
        setPainelDeVoz(true);
        void conversaPorVoz.iniciar();
    };

    /* Abrir direto na voz: decide uma vez só, quando os modelos chegam; sem voz, fica o chat. */
    const vozAutomaticaDecidida = useRef(false);
    useEffect(() => {
        if (vozAutomaticaDecidida.current || !modelos) return;
        vozAutomaticaDecidida.current = true;
        if (!abrirNaVoz || !celular || somenteLeitura || !conversaPorVoz.suportada || !modelos.voz.transcrever) return;
        iniciarVoz();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- roda só na chegada dos modelos
    }, [modelos]);

    /* Só a voz da VecchIA: sem escolha de voz, o aviso de requisitos já inicia a conversa. */
    const depoisDoAviso = () => {
        iniciarVoz();
    };

    const alternarDitado = async () => {
        if (!ditado.suportado) {
            aviso({ tone: 'danger', title: 'Ditado por voz requer Chrome ou Edge com microfone.' });
            return;
        }
        if (ditado.salvando) return;
        if (ditado.gravando) {
            ditado.cancelar();
            return;
        }
        try {
            await ditado.iniciar(mensagem);
            focarCampo();
        } catch (e) {
            aviso({ tone: 'danger', title: mensagemDe(e) });
        }
    };

    const salvarDitado = async () => {
        if (!ditado.gravando || ditado.salvando) return;
        const final = await ditado.salvar();
        if (!final.trim()) aviso({ tone: 'danger', title: 'Nenhuma fala detectada. Tente de novo mais perto do microfone.' });
        focarCampo();
    };

    /* ------------------------------------------------------------ campo */

    function ajustarAltura() {
        const el = campo.current;
        if (!el) return;
        el.style.height = '36px';
        el.style.height = `${String(Math.min(el.scrollHeight, 160))}px`;
    }

    function focarCampo() {
        setTimeout(() => {
            const el = campo.current;
            if (!el) return;
            el.focus();
            el.selectionStart = el.value.length;
            el.selectionEnd = el.value.length;
            ajustarAltura();
        }, 0);
    }

    useEffect(() => {
        if (ditado.gravando) ajustarAltura();
    }, [mensagem, ditado.gravando]);

    const aoTeclar = (e: EventoDeTecla<HTMLTextAreaElement>) => {
        if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
        /* No teclado do celular o Enter quebra linha; envia pelo botão. */
        if (toque) return;
        e.preventDefault();
        void enviarDoCampo();
    };

    const enviarDoCampo = async (texto?: string) => {
        const conteudo = texto ?? mensagem;
        if (!conteudo.trim() && anexos.length === 0) return;
        if (toque) campo.current?.blur();
        await enviar(conteudo);
    };

    /* Menu de idioma. */
    const rectDoIdioma = useRetanguloDaAncora(botaoIdioma.current, menuIdioma);
    useFecharPorFora(menuIdioma, (alvo) => Boolean(menuDeIdioma.current?.contains(alvo) || botaoIdioma.current?.contains(alvo)), () => setMenuIdioma(false), botaoIdioma.current);
    useEffect(() => {
        if (menuIdioma) setTimeout(() => menuDeIdioma.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus(), 0);
    }, [menuIdioma]);

    /* ------------------------------------------------------------ derivados */

    const temMensagens = mensagens.length > 0;
    const acoes = sugestoesDoContexto && sugestoesDoContexto.length > 0 ? sugestoesDoContexto : ACOES_RAPIDAS;
    const anexosDaConversa = useMemo(() => mensagens.flatMap((m) => m.anexos), [mensagens]);
    const arquivosDaConversa = useMemo(() => mensagens.flatMap((m) => m.arquivos), [mensagens]);
    const totalDaBiblioteca = anexosDaConversa.length + arquivosDaConversa.length;
    const nomeDoModelo = nomeDaEscolha(escolha, modelos);
    const bloqueado = Boolean(somenteLeitura);

    const propostaMudou = (mensagemId: string, nova: PropostaDoBrain) => {
        setMensagens((atuais) => atuais.map((m) => (m.id !== mensagemId ? m : { ...m, propostas: m.propostas.map((p) => (p.id === nova.id ? nova : p)) })));
        setAnuncio(nova.situacao === 'confirmada' ? 'Ação confirmada e executada.' : nova.situacao === 'cancelada' ? 'Ação cancelada. Nada foi alterado.' : nova.situacao === 'expirada' ? 'A proposta expirou. Nada foi alterado.' : nova.situacao === 'falhou' ? 'A ação falhou.' : 'A ação continua pendente.');
    };

    /* As decisões dentro da conversa por voz: o cartão sobre a tela de voz e a frase que decide. */
    const acoesDaVoz = useAcoesDaVoz({
        ativa: painelDeVoz, mensagens, fase: conversaPorVoz.fase, somenteLeitura, idioma,
        aoMudarProposta: propostaMudou, anunciar: conversaPorVoz.anunciar,
    });
    resolverFalaDaVoz.current = acoesDaVoz.resolverFala;

    /* ------------------------------------------------------------ desenho */

    const caixaDoCampo = (inferior: boolean) => (
        <div className={b.inputContainer} style={inferior ? { marginBottom: 0, maxWidth: 600 } : undefined}>
            <div className={b.composerShell}>
                <div className={b.inputBox} ref={caixa}>
                    {(ferramenta || anexos.length > 0 || edicao) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingBottom: 4 }}>
                            {edicao && (
                                <button type="button" className={b.toolChip} onClick={cancelarEdicao} aria-label="Cancelar a edição da mensagem">
                                    <Icon name="pencil" size={10} />
                                    Editando mensagem
                                    <Icon name="x" size={10} />
                                </button>
                            )}
                            {ferramenta && (
                                <button type="button" className={b.toolChip} onClick={() => setFerramenta(null)} aria-label={`Tirar ${ferramenta === 'web' ? 'Buscar na web' : 'Buscar em documentos'}`}>
                                    <Icon name={ferramenta === 'web' ? 'globe' : 'book-open'} size={10} />
                                    {ferramenta === 'web' ? 'Buscar na web' : 'Buscar em documentos'}
                                    <Icon name="x" size={10} />
                                </button>
                            )}
                            {anexos.map((a) => {
                                const curto = a.rotulo.length > 22 ? `${a.rotulo.slice(0, 19)}…` : a.rotulo;
                                return (
                                    <button key={a.id} type="button" className={b.toolChip} onClick={() => tirarAnexo(a.id)} aria-label={`Tirar o anexo ${a.rotulo}`}>
                                        {a.previa ? <img className="brain-shell__tool-chip-thumb" src={a.previa} alt="" /> : <Icon name={a.origem === 'aprender' ? 'graduation-cap' : 'file-text'} size={10} />}
                                        {curto}
                                        <Icon name="x" size={10} />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {ditado.gravando && (
                        <div className={b.voiceRecordingBar} role="status">
                            <span className={b.voiceRecordingDot} />
                            <span>{ditado.pausado ? 'Pausado' : 'Gravando'} {formatarDuracao(ditado.duracao)}</span>
                            <Dica titulo={ditado.pausado ? 'Retomar' : 'Pausar'}>
                                <button type="button" className={b.voiceIconBtn} onClick={() => { if (ditado.pausado) ditado.retomar(); else ditado.pausar(); }} disabled={ditado.salvando} aria-label={ditado.pausado ? 'Retomar o ditado' : 'Pausar o ditado'}>
                                    <Icon name={ditado.pausado ? 'play' : 'pause'} size={11} />
                                </button>
                            </Dica>
                            <Dica titulo="Salvar">
                                <button type="button" className={b.voiceIconBtnSave} onClick={() => { void salvarDitado(); }} disabled={ditado.salvando} aria-label="Salvar o ditado">
                                    {ditado.salvando ? <Girador tamanho={10} /> : <Icon name="check" size={11} />}
                                </button>
                            </Dica>
                            <Dica titulo="Cancelar">
                                <button type="button" className={b.voiceIconBtnCancel} onClick={() => { ditado.cancelar(); focarCampo(); }} disabled={ditado.salvando} aria-label="Cancelar o ditado">
                                    <Icon name="x" size={11} />
                                </button>
                            </Dica>
                        </div>
                    )}
                    <textarea
                        ref={campo} className={b.textArea} aria-label="Mensagem para a VecchIA" rows={1}
                        placeholder={bloqueado ? 'Somente leitura nesta clínica' : ditado.gravando ? 'Falando… o texto aparece aqui em tempo real' : 'Pergunte algo à VecchIA...'}
                        value={mensagem} onChange={(e) => setMensagem(e.target.value)} onKeyDown={aoTeclar} onInput={ajustarAltura}
                        disabled={ditado.salvando || bloqueado}
                    />
                    <div className={b.inputFooter}>
                        <div className={b.inputFooterLeft}>
                            <Dica titulo="Anexar">
                                <button
                                    ref={botaoMais} type="button" className={b.iconBtn} aria-label="Anexar e ferramentas" aria-haspopup="menu" aria-expanded={menuMais}
                                    onClick={(e) => { e.stopPropagation(); setMenuMais((v) => !v); }} disabled={ditado.gravando || ditado.salvando || bloqueado}
                                >
                                    <Icon name="plus" size={13} />
                                </button>
                            </Dica>
                            <AssistenteDeTexto
                                texto={mensagem} aoMudarTexto={setMensagem} desabilitado={carregando || ditado.gravando || ditado.salvando || bloqueado}
                                ancoraDoBalao={caixa.current} aoFocarCampo={focarCampo} idioma={idioma} escuro={escuro}
                                aoErro={(titulo, detalhe) => aviso({ tone: 'danger', title: titulo, description: detalhe })}
                            />
                            <SeletorDeModelo modelos={modelos} escolha={escolha} aoEscolher={setEscolha} />
                        </div>
                        <div className={b.inputFooterRight}>
                            {ditado.suportado && (
                                <Dica titulo={ditado.gravando ? 'Cancelar ditado' : 'Ditado por voz (tempo real)'}>
                                    <button
                                        type="button" className={ditado.gravando ? b.iconBtnRecording : b.iconBtn} aria-label={ditado.gravando ? 'Cancelar ditado' : 'Ditado por voz'}
                                        onClick={() => { void alternarDitado(); }} disabled={carregando || ditado.salvando || painelDeVoz || bloqueado}
                                    >
                                        <Icon name="mic" size={12} />
                                    </button>
                                </Dica>
                            )}
                            <Dica titulo="Idioma">
                                <button ref={botaoIdioma} type="button" className={b.iconBtn} disabled={ditado.gravando} aria-label={`Idioma: ${IDIOMAS.find((i) => i.codigo === idioma)?.rotulo ?? idioma}`} aria-haspopup="menu" aria-expanded={menuIdioma} onClick={(e) => { e.stopPropagation(); setMenuIdioma((v) => !v); }}>
                                    <Icon name="globe" size={12} />
                                </button>
                            </Dica>
                            {carregando ? (
                                <Dica titulo="Parar geração">
                                    <button type="button" className={b.iconBtn} onClick={parar} aria-label="Parar geração">
                                        <Icon name="pause" size={12} />
                                    </button>
                                </Dica>
                            ) : (
                                <button type="button" className={b.sendBtn} onClick={() => { void enviarDoCampo(); }} aria-label="Enviar mensagem" disabled={(!mensagem.trim() && anexos.length === 0) || ditado.gravando || ditado.salvando || bloqueado}>
                                    <Icon name="send" size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const acaoRapida = (acao: AcaoRapida) => (
        <button type="button" key={acao.titulo} className={b.quickActionCard} onClick={() => { void enviarDoCampo(acao.prompt); }} disabled={bloqueado}>
            <span className={b.quickActionIcon} aria-hidden="true"><Icon name={acao.icone} size={10} /></span>
            <span className={b.quickActionTitle}>{acao.titulo}</span>
        </button>
    );

    const conteudoPrincipal = () => {
        if (visao === 'conversas') {
            return (
                <PaginaDeConversas
                    conversas={conversas} carregando={carregandoConversas} ativa={conversaAtiva?.id ?? null}
                    aoAbrir={(id) => { void abrirConversa(id); }} aoNova={() => { novaConversa(); setVisao('chat'); }}
                />
            );
        }
        if (visao === 'projetos') {
            return <PaginaDeProjetos projetos={projetos} ativo={projetoAtivo} carregando={carregandoProjetos} aoEscolher={escolherProjetoDaLista} aoCriar={() => setDialogoDeProjetos(true)} />;
        }
        if (visao === 'personalizar') {
            return (
                <CentralDePersonalizacao
                    secao={secao} aoTrocarSecao={setSecao} preferencias={preferencias}
                    aoMudar={mudarPreferencias} aoSalvar={salvarPreferenciasAgora} somenteLeitura={bloqueado}
                />
            );
        }
        if (!temMensagens) {
            return (
                <div className={b.welcomeContainer}>
                    <div className={b.greetingTitle}>Olá {primeiroNome}, como posso te ajudar?</div>
                    <p className="brain-apresentacao">VecchIA — assistente generativa que pode fazer praticamente tudo para sua clínica</p>
                    <AvisoDeCanais somenteLeitura={somenteLeitura} />
                    <div className={b.welcomeComposerBlock}>
                        {caixaDoCampo(false)}
                        {/* No celular os cartões rolam de lado (como no original), com a borda esmaecida avisando. */}
                        <div className={b.quickActionsGrid} data-rolagem="horizontal">{acoes.map(acaoRapida)}</div>
                    </div>
                </div>
            );
        }
        return (
            <div className={b.chatContainer}>
                <div className={b.messagesArea} role="log" aria-label="Mensagens">
                    {mensagens.map((m, i) => (m.oculta ? null : (
                        <div key={m.id} className={m.papel === 'usuario' ? b.messageRowUser : b.messageRowAssistant}>
                            {m.papel === 'assistente' && <div className={b.messageLabelAssistant}>{assistente}</div>}
                            {m.papel === 'assistente' && etiquetasDa(m).length > 0 && (
                                <div style={{ marginBottom: 4 }}>
                                    {etiquetasDa(m).map((t) => <span key={t} className={b.toolBadge}><Icon name="zap" size={8} /> {t}</span>)}
                                </div>
                            )}
                            {m.papel === 'usuario' ? (
                                <div className={b.messageRowUserInner}>
                                    <div className={b.messageUserHeader}>
                                        <span className={b.messageLabelUser}>Você</span>
                                        <div className={b.messageUserActions}>
                                            <Dica titulo="Editar e reenviar">
                                                <button type="button" className={b.messageActionBtn} onClick={() => editarMensagem(i)} disabled={carregando || Boolean(m.local) || bloqueado} aria-label="Editar e reenviar">
                                                    <Icon name="pencil" size={11} />
                                                </button>
                                            </Dica>
                                        </div>
                                    </div>
                                    <PrevisaoDosAnexos anexos={m.anexos} />
                                    {m.conteudo && (
                                        <div className={b.messageContentUser}>
                                            <div style={{ whiteSpace: 'pre-wrap' }}>{m.conteudo}</div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {m.conteudo && (
                                        <div className={b.messageContentAssistant}>
                                            <MarkdownSeguro texto={m.conteudo} />
                                        </div>
                                    )}
                                    {m.interrompida && (
                                        <button type="button" className="brain-texto-link" style={{ fontSize: 12, marginTop: 4 }} onClick={() => { void verSeFicouPronta(m.interrompida ?? ''); }}>
                                            Ver se a resposta ficou pronta
                                        </button>
                                    )}
                                    {m.arquivos.map((a) => (
                                        <button key={a.id} type="button" className="brain-resposta__arquivo" onClick={() => setArquivoAberto(a)} aria-label={`Ver o arquivo ${a.nome}`}>
                                            <Icon name={iconeDoArquivo(tipoDoArquivo(a))} size={13} />
                                            <span>{a.nome}</span>
                                        </button>
                                    ))}
                                    {m.propostas.map((p) => (
                                        <Fragment key={p.id}>
                                            <CartaoDeAcao proposta={p} somenteLeitura={somenteLeitura} aoMudar={(nova) => propostaMudou(m.id, nova)} />
                                            {p.ferramenta === 'propor_reconectar_canal' && p.situacao === 'confirmada' && p.canalId && (
                                                <CanalDaProposta canalId={p.canalId} somenteLeitura={somenteLeitura} />
                                            )}
                                        </Fragment>
                                    ))}
                                    {((m.fontes?.length ?? 0) > 0 || (m.documentos?.length ?? 0) > 0) && (
                                        <div className="brain-resposta__extras">
                                            {m.fontes?.map((f) => (
                                                <a key={f.url} className="brain-resposta__fonte" href={f.url} target="_blank" rel="noopener noreferrer">
                                                    <Icon name="globe" size={10} /> {f.titulo}
                                                </a>
                                            ))}
                                            {m.documentos?.map((d) => (
                                                <span key={d.titulo} className="brain-resposta__fonte"><Icon name="book-open" size={10} /> {d.titulo}</span>
                                            ))}
                                        </div>
                                    )}
                                    {m.avisos?.map((a) => <p key={a} className="brain-resposta__aviso">{a}</p>)}
                                    {!m.local && m.conteudo && (
                                        <div className="brain-resposta__acoes">
                                            <BotaoOuvir texto={m.conteudo} idioma={idioma} />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )))}
                    {carregando && (
                        <div className={b.messageRowAssistant}>
                            <div className={b.messageLabelAssistant}>{assistente}</div>
                            <div className={b.workingBar}>
                                <div className={b.typingIndicator} style={{ margin: 0 }} role="status" aria-label={`${assistente} está pensando`}><span /><span /><span /></div>
                            </div>
                        </div>
                    )}
                    <div ref={fim} />
                </div>
                <div className={`${b.bottomInput}${menuMais ? ` ${b.bottomInputMenuOpen}` : ''}`}>
                    {caixaDoCampo(true)}
                </div>
            </div>
        );
    };

    const itemDoMenu = (rotulo: string, icone: string, aoClicar: () => void, ativo = false) => (
        <Dica titulo={barraAberta ? '' : rotulo} lado="right">
            <button
                type="button" className={`${b.sidebarMenuItem} ${ativo ? b.sidebarMenuItemActive : ''}`} aria-label={rotulo} aria-current={ativo ? 'page' : undefined}
                onClick={() => { aoClicar(); if (celular) setBarraAberta(false); }}
            >
                <Icon name={icone} size={14} />
                <span className={b.sidebarMenuItemLabel}>{rotulo}</span>
            </button>
        </Dica>
    );

    const raiz = `${escuro ? 'brain-shell brain-shell--dark' : 'brain-shell'} ${b.root}`;

    return (
        <div className={raiz}>
            <input type="file" ref={seletorDeArquivo} style={{ display: 'none' }} multiple accept={aceita || undefined} onChange={aoEscolherArquivos} tabIndex={-1} aria-hidden="true" />
            <MenuDoMais
                aberto={menuMais} ancora={botaoMais.current} acoplado={temMensagens} aoFechar={() => setMenuMais(false)}
                aoEscolherTipo={escolherTipo} aoCapturarTela={() => { void capturarTela(); }}
                ferramenta={ferramenta} aoEscolherFerramenta={setFerramenta}
                projetos={projetos} projetoAtivo={projetoAtivo} aoEscolherProjeto={(id) => { void escolherProjetoDoMenu(id); }}
                aoCriarProjeto={criarProjeto} aoAprenderLink={aprenderLink}
            />

            {celular && barraAberta && (
                <div className="brain-shell__sidebar-backdrop" role="presentation" onClick={() => setBarraAberta(false)} />
            )}
            {(
                <nav
                    aria-label="Menu da VecchIA"
                    className={`${b.sidebar} ${!barraAberta ? b.sidebarCollapsed : ''}${celular ? ' brain-shell__sidebar--mobile' : ''}`}
                >
                    <div className={b.sidebarHeader}>
                        <div className={b.sidebarBrandRow}>
                            <button type="button" className={b.sidebarBrandLogo} onClick={() => { if (!barraAberta) setBarraAberta(true); }} aria-label={barraAberta ? 'VecchIA' : 'Expandir menu'}>
                                <LogoDoBrain tamanho={barraAberta ? 28 : 26} />
                            </button>
                        </div>
                        {barraAberta && (
                            <Dica titulo="Recolher menu">
                                <button type="button" className={b.sidebarCollapseBtn} onClick={() => setBarraAberta(false)} aria-label="Recolher menu">
                                    <Icon name="panel-left-close" size={15} />
                                </button>
                            </Dica>
                        )}
                    </div>
                    <div className={b.sidebarMenu}>
                        {itemDoMenu('Nova conversa', 'plus', () => { setVisao('chat'); novaConversa(); })}
                        {barraAberta && itemDoMenu('Buscar conversas', 'search', () => setVisao('conversas'), visao === 'conversas')}
                        {barraAberta && itemDoMenu('Projetos', 'folder-kanban', () => setVisao('projetos'), visao === 'projetos')}
                        {itemDoMenu('Personalizar', 'sliders-horizontal', () => { setSecao('hub'); setVisao('personalizar'); }, visao === 'personalizar')}
                    </div>
                    <div className={b.sidebarRecentSection}>
                        <div className={b.sidebarSectionLabel}>
                            {projetoAtivo ? `Recentes · ${projetos.find((p) => p.id === projetoAtivo)?.nome ?? 'projeto'}` : 'Recentes'}
                        </div>
                        <div className={b.sidebarRecentList}>
                            {carregandoConversas && <div className={b.loadingCenter}><Girador tamanho={16} /></div>}
                            {!carregandoConversas && conversas.length === 0 && (
                                <span className={b.sidebarEmpty}>{projetoAtivo ? 'Nenhuma conversa neste projeto ainda.' : 'Nenhuma conversa ainda.'}</span>
                            )}
                            {conversas.slice(0, 40).map((c) => (
                                <div key={c.id} className={`${b.conversationRecentRow} ${conversaAtiva?.id === c.id ? b.conversationRecentItemActive : ''}`}>
                                    {renomeando === c.id ? (
                                        <>
                                            <input
                                                className={b.conversationRecentEditInput} value={tituloNovo} maxLength={120} aria-label="Novo nome da conversa"
                                                onChange={(e) => setTituloNovo(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') { e.preventDefault(); void salvarTitulo(c.id); }
                                                    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setRenomeando(null); }
                                                }}
                                                ref={(el) => { if (el && document.activeElement !== el) el.focus(); }}
                                            />
                                            <div className={b.conversationRecentActions} style={{ opacity: 1, pointerEvents: 'auto' }}>
                                                <Dica titulo="Salvar">
                                                    <button type="button" className={b.conversationRecentActionBtn} onClick={() => { void salvarTitulo(c.id); }} aria-label="Salvar o nome">
                                                        <Icon name="check" size={11} />
                                                    </button>
                                                </Dica>
                                                <Dica titulo="Cancelar">
                                                    <button type="button" className={b.conversationRecentActionBtn} onClick={() => setRenomeando(null)} aria-label="Cancelar a renomeação">
                                                        <Icon name="x" size={11} />
                                                    </button>
                                                </Dica>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button" className={b.conversationRecentTitle} title={c.titulo} aria-current={conversaAtiva?.id === c.id ? 'true' : undefined}
                                                onClick={() => { void abrirConversa(c.id); }}
                                            >
                                                <span className="brain-shell__conversation-recent-title-text">{c.titulo || 'Conversa sem título'}</span>
                                            </button>
                                            <div className={b.conversationRecentActions}>
                                                <Dica titulo="Renomear">
                                                    <button type="button" className={b.conversationRecentActionBtn} onClick={() => { setRenomeando(c.id); setTituloNovo(c.titulo); }} aria-label={`Renomear a conversa ${c.titulo}`} disabled={bloqueado}>
                                                        <Icon name="pencil" size={11} />
                                                    </button>
                                                </Dica>
                                                <Dica titulo="Excluir">
                                                    <button type="button" className={b.conversationRecentActionBtn} onClick={() => setApagando(c)} aria-label={`Apagar a conversa ${c.titulo}`} disabled={bloqueado}>
                                                        <Icon name="trash-2" size={11} />
                                                    </button>
                                                </Dica>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p className={b.sidebarEmpty} style={{ marginTop: 8 }}>As conversas são apagadas depois de 90 dias.</p>
                    </div>
                </nav>
            )}

            <div
                className={`${b.mainContent}${visao === 'chat' ? ` ${b.mainContentChat}` : ''}${temMensagens && visao === 'chat' ? ` ${b.mainContentConversation}` : ''}${painelDeVoz ? ` ${b.mainContentVoice}` : ''}`}
            >
                <ModoDeVoz aberto={painelDeVoz} conversa={conversaPorVoz} acoes={acoesDaVoz} somenteLeitura={somenteLeitura} aoFechar={fecharVoz} />
                <header className={b.chatHeader}>
                    <div className={b.chatHeaderBrand}>
                        {celular && (
                            <button type="button" className="brain-shell__mobile-menu-btn" onClick={() => setBarraAberta(true)} aria-label="Abrir menu">
                                <Icon name="menu" size={20} />
                            </button>
                        )}
                        <MenuDaConta
                            titulo="VecchIA" pessoa={sessao?.nome ?? ''} clinica={sessao?.clinica ?? ''} escuro={escuro}
                            aoVoltar={() => { if (sobreposta && aoFechar) { aoFechar(); return; } navigate('/app'); }}
                        />
                        {temMensagens && conversaAtiva?.titulo && visao === 'chat' && (
                            <span className={b.chatHeaderTitle}> / {conversaAtiva.titulo}</span>
                        )}
                    </div>
                    <div className={b.chatHeaderRight}>
                        {temMensagens && visao === 'chat' && (
                            <div className={b.chatHeaderTools}>
                                <span className={b.modelBadge}>{nomeDoModelo}</span>
                                {totalDaBiblioteca > 0 && (
                                    <Dica titulo="Biblioteca de anexos">
                                        <button type="button" className={b.attachLibraryBtn} onClick={() => setBibliotecaAberta(true)} aria-label={`Biblioteca de anexos: ${String(totalDaBiblioteca)}`}>
                                            <Icon name="book-open" size={13} />
                                            <span className={b.attachLibraryBadge}>{totalDaBiblioteca}</span>
                                        </button>
                                    </Dica>
                                )}
                                <Dica titulo="Nova conversa">
                                    <button type="button" className={b.iconBtnPlain} onClick={novaConversa} aria-label="Nova conversa">
                                        <Icon name="plus" size={13} />
                                    </button>
                                </Dica>
                            </div>
                        )}
                        <div className={b.chatHeaderActionsPlain}>
                            {conversaPorVoz.suportada && visao === 'chat' && !bloqueado && (
                                <Dica titulo={painelDeVoz ? 'Encerrar conversa por voz' : 'Conversa por voz'}>
                                    <button type="button" onClick={alternarVoz} className={painelDeVoz ? b.voiceModeBtnActive : b.voiceModeBtn} aria-label={painelDeVoz ? 'Encerrar conversa por voz' : 'Conversa por voz'} aria-pressed={painelDeVoz}>
                                        <Icon name="audio-lines" size={14} />
                                    </button>
                                </Dica>
                            )}

                            <Dica titulo="Ajuda da VecchIA">
                                <button type="button" className={b.helpBtnPlain} onClick={() => setAjuda(true)} aria-label="Ajuda da VecchIA">
                                    <Icon name="circle-help" size={15} />
                                </button>
                            </Dica>
                            {sobreposta && aoFechar && (
                                <Dica titulo="Fechar e voltar">
                                    <button type="button" className="brain-fechar" onClick={aoFechar} aria-label="Fechar a VecchIA">
                                        <Icon name="x" size={18} />
                                    </button>
                                </Dica>
                            )}
                        </div>
                    </div>
                </header>

                {demonstracao && (
                    <div className="brain-camada-aviso" role="note">
                        <Icon name="info" size={14} />
                        <span>Demonstração: as respostas são simuladas aqui mesmo, sem IA e sem rede. Nada do que você escrever ou anexar sai deste navegador.</span>
                    </div>
                )}
                {somenteLeitura && (
                    <div className="brain-camada-aviso" role="note">
                        <Icon name="eye" size={14} />
                        <span>{somenteLeitura}</span>
                    </div>
                )}
                {erro && (
                    <div className={`brain-camada-aviso${erro.tom === 'danger' ? ' brain-camada-aviso--erro' : ''}`} role="alert">
                        <Icon name="circle-alert" size={14} />
                        <span><strong>{erro.titulo}.</strong> {erro.detalhe}</span>
                        <button type="button" onClick={() => setErro(null)} aria-label="Fechar o aviso"><Icon name="x" size={12} /></button>
                    </div>
                )}

                {conteudoPrincipal()}
            </div>

            <AvisoDaVoz aberto={avisoDaVoz} aoFechar={() => setAvisoDaVoz(false)} aoContinuar={depoisDoAviso} />

            <DialogoDeProjetos
                aberto={dialogoDeProjetos} aoFechar={() => setDialogoDeProjetos(false)} projetos={projetos} ativo={projetoAtivo}
                aoEscolher={escolherProjetoDaLista} aoCriar={criarProjeto}
            />
            <FolhaDoArquivo arquivo={arquivoAberto} aoFechar={() => setArquivoAberto(null)} />
            <BibliotecaDeAnexos
                aberta={bibliotecaAberta} aoFechar={() => setBibliotecaAberta(false)} gerados={arquivosDaConversa} enviados={anexosDaConversa}
                aoAbrirGerado={(a) => { setBibliotecaAberta(false); setArquivoAberto(a); }}
            />
            <AjudaDoBrain aberta={ajuda} aoFechar={() => setAjuda(false)} />
            <ConfirmarNoBrain
                aberto={apagando !== null} titulo="Apagar esta conversa?" rotuloDaAcao="Apagar conversa"
                descricao={`“${apagando?.titulo ?? ''}” e as mensagens dela deixam de existir para você. Não dá para desfazer.`}
                aoConfirmar={() => { void confirmarApagar(); }} aoCancelar={() => setApagando(null)}
            />

            {menuIdioma && rectDoIdioma && createPortal(
                // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- o foco vai para os itens, não para o contêiner
                <div ref={menuDeIdioma} role="menu" aria-label="Idioma" className="brain-dropdown" onKeyDown={setasDoIdioma} style={estiloAncorado(rectDoIdioma, { minWidth: 190, placement: 'above', offset: 4, align: 'right' })}>
                    {IDIOMAS.map((i) => (
                        <button
                            key={i.codigo} type="button" role="menuitemradio" aria-checked={idioma === i.codigo} className={`brain-dropdown__item ${b.modelMenuItem} brain-idioma__item`}
                            onClick={() => { setMenuIdioma(false); mudarPreferencias({ ...preferencias, idioma: i.codigo }); botaoIdioma.current?.focus(); }}
                        >
                            <span>{i.rotulo}</span>
                            {idioma === i.codigo && <Icon name="check" size={13} strokeColor="#57534e" />}
                        </button>
                    ))}
                </div>,
                document.body,
            )}

            <div role="status" aria-live="polite" className="brain-so-leitor">{anuncio}</div>
        </div>
    );
}
